import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  adminPlatformRoom,
  connectRealtimeWhenAvailable,
  getAccessToken,
  getRealtimeUrl,
  isRealtimeEnabled,
  subscribeRooms,
} from '@beatq/shared';
import {
  createAdminRealtimeConnectionTicket,
  parseAdminRealtimeTicketEnvelope,
} from './adminRealtimeApi.js';

export { parseAdminRealtimeTicketEnvelope };
export const parseRealtimeTicketEnvelope = parseAdminRealtimeTicketEnvelope;

const RETRY_MS = 20_000;

const PROTOCOL_EVENTS = new Set([
  'connect',
  'disconnect',
  'connect_error',
  'subscribed',
  'reconnect_attempt',
  'reconnect_error',
  'reconnect_failed',
]);

/**
 * Query prefixes invalidated on `admin:platform`. Spec list plus 042/046/048
 * prefixes so screens that call the hook without extraQueryKeys still refresh.
 */
export const ADMIN_PLATFORM_REALTIME_QUERY_KEYS = [
  ['admin', 'dashboard-summary'],
  ['admin', 'cases'],
  ['admin', 'moderation-queue'],
  ['admin', 'fraud-queue'],
  ['admin', 'risk-cases'],
  ['admin', 'notification-inbox'],
  ['admin', 'shell', 'emergency-status'],
  ['admin', 'emergency'],
  ['admin', 'approval-inbox'],
  ['admin', 'emergency-status'],
  ['admin', 'notification-health'],
  ['admin', 'notification-suppressions'],
  ['admin', 'notification-history'],
  ['admin', 'delivery-failure'],
  ['admin', 'delivery-diagnostics'],
  ['admin', 'refund-offer'],
  ['admin', 'remedy-history'],
] as const;

export type AdminRealtimeStatus = {
  connected: boolean;
  subscribed: boolean;
};

type AdminOpsSocket = NonNullable<Awaited<ReturnType<typeof connectRealtimeWhenAvailable>>>;

function extraRoomList(extra?: string | string[] | null): string[] {
  if (extra == null) return [];
  return (Array.isArray(extra) ? extra : [extra]).filter((room) => room.length > 0);
}

function roomsFor(extra?: string | string[] | null): string[] {
  return [...new Set([adminPlatformRoom(), ...extraRoomList(extra)])];
}

/**
 * Mint an admin realtime ticket, subscribe to `admin:platform` (+ extra rooms),
 * and invalidate operator queues on room events. Poll on the page remains the fallback.
 * `connected` is true only while the Socket.IO connection is actually connected.
 */
export function useAdminPlatformRealtime(options?: {
  extraRooms?: string | string[] | null;
  extraQueryKeys?: readonly (readonly unknown[])[];
}): AdminRealtimeStatus {
  const qc = useQueryClient();
  const qcRef = useRef(qc);
  qcRef.current = qc;
  const extraKeysRef = useRef(options?.extraQueryKeys);
  extraKeysRef.current = options?.extraQueryKeys;
  const extraRoomsRef = useRef(options?.extraRooms);
  extraRoomsRef.current = options?.extraRooms;

  const extraRoomKey =
    options?.extraRooms == null
      ? ''
      : Array.isArray(options.extraRooms)
        ? options.extraRooms.filter(Boolean).join('|')
        : options.extraRooms;

  const [connected, setConnected] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (!isRealtimeEnabled()) {
      setConnected(false);
      setSubscribed(false);
      return;
    }

    let cancelled = false;
    let socket: AdminOpsSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const invalidate = () => {
      for (const queryKey of ADMIN_PLATFORM_REALTIME_QUERY_KEYS) {
        void qcRef.current.invalidateQueries({ queryKey: [...queryKey] });
      }
      for (const queryKey of extraKeysRef.current ?? []) {
        void qcRef.current.invalidateQueries({ queryKey: [...queryKey] });
      }
    };

    const onAny = (eventName: string) => {
      if (PROTOCOL_EVENTS.has(eventName)) return;
      invalidate();
    };

    const onConnect = () => {
      if (cancelled || !socket) return;
      setConnected(true);
      void subscribeRooms(socket, roomsFor(extraRoomsRef.current)).then((result) => {
        if (!cancelled) setSubscribed(result.rooms.length > 0);
      });
    };

    const onDisconnect = () => {
      if (cancelled) return;
      setConnected(false);
      setSubscribed(false);
    };

    const onConnectError = () => {
      if (cancelled) return;
      setConnected(false);
    };

    const detach = () => {
      if (!socket) return;
      socket.offAny(onAny);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
    };

    const attempt = async () => {
      let minted: ReturnType<typeof parseAdminRealtimeTicketEnvelope> = null;
      try {
        const envelope = await createAdminRealtimeConnectionTicket();
        minted = parseAdminRealtimeTicketEnvelope(envelope);
      } catch {
        // Mint failed (403/404/network) — JWT handshake is valid per 10-11.
      }
      if (cancelled) return;

      const next = await connectRealtimeWhenAvailable({
        surface: 'admin',
        accessToken: getAccessToken() ?? undefined,
        ...(minted?.ticket ? { ticket: minted.ticket } : {}),
        url: minted?.websocketUrl ?? getRealtimeUrl(),
      });
      if (cancelled) return;
      if (!next) {
        setConnected(false);
        setSubscribed(false);
        if (cancelled) return;
        retryTimer = setTimeout(() => {
          void attempt();
        }, RETRY_MS);
        return;
      }

      if (retryTimer) clearTimeout(retryTimer);
      socket = next;
      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('connect_error', onConnectError);
      socket.onAny(onAny);

      const result = await subscribeRooms(socket, roomsFor(extraRoomsRef.current));
      if (cancelled) return;
      setConnected(Boolean(socket.connected));
      setSubscribed(socket.connected && result.rooms.length > 0);
    };

    void attempt();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      setConnected(false);
      setSubscribed(false);
      detach();
    };
  }, [extraRoomKey]);

  return { connected, subscribed };
}
